import { useMemo } from 'react';
import { X, Plus, CheckCheck, Loader, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { useListCustomers } from '@/lib/api/customer';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import { useQueryParam } from '@/hooks/use-query-param';
import { useQueryParamArray } from '@/hooks/use-query-param-array';
import { Button } from '@/components/ui/button';
import { ComboboxMulti } from '@/components/ui/combobox_mutli';
import { useSearchParams } from 'react-router-dom';
import { CropYearSelect } from './CropYearSelect';
import { CreateCropProfileDialog } from '@/section/risk/CropProfileForm';
import { cn } from "@/lib/utils";
import { generateCurrentDefaultCommodityYear } from "./dates";

interface CustomersFiltersProps {
    isDataLoading: boolean;
    hasError: boolean;
    customerOptions: { value: string; label: string }[];
    customerIds: string[];
    setCustomerIds: (val: string[]) => void;
}

function CustomersFilters({ 
    isDataLoading, 
    hasError, 
    customerOptions, 
    customerIds, 
    setCustomerIds 
}: CustomersFiltersProps) {
    return (
        <div className="relative">
            <label htmlFor="customerFilter" className="hidden md:block text-sm font-medium text-gray-700">
                Customers {isDataLoading && <Loader className="inline w-3 h-3 animate-spin ml-1" />}
            </label>
            <ComboboxMulti
                id="customerFilter"
                dwimOnChange
                virtualizeList
                disabled={hasError}
                options={customerOptions}
                value={customerIds}
                onValueChange={(val) => setCustomerIds(val)}
                placeholder={isDataLoading ? "Loading customers..." : hasError ? "Error loading customers" : "Select Customer"}
                className="w-full"
                popoverClassName='min-w-[280px] sm:min-w-[320px] md:min-w-[380px] lg:min-w-[420px]'
                showSelectAll={false}
            />
        </div>
    );
}

interface ImportantFiltersProps {
    overviewTab: string;
    isDataLoading: boolean;
    hasError: boolean;
    traderOptions: { value: string; label: string }[];
    customerOptions: { value: string; label: string }[];
    traderIds: string[];
    customerIds: string[];
    selectedCropYear: string;
    setTraderIds: (val: string[]) => void;
    setCustomerIds: (val: string[]) => void;
    setSelectedCropYear: (val: string) => void;
}

function ImportantFilters({
    overviewTab,
    isDataLoading,
    hasError,
    traderOptions,
    customerOptions,
    traderIds,
    customerIds,
    selectedCropYear,
    setTraderIds,
    setCustomerIds,
    setSelectedCropYear
}: ImportantFiltersProps) {
    if (overviewTab === 'unknown') {
        return (
            <CustomersFilters
                isDataLoading={isDataLoading}
                hasError={hasError}
                customerOptions={customerOptions}
                customerIds={customerIds}
                setCustomerIds={setCustomerIds}
            />
        );
    }

    return (
        <>
            {!['unknown'].includes(overviewTab) && (
                <div className="relative">
                    <label htmlFor="tradersFilter" className="block text-sm font-medium text-gray-700">
                        Traders {isDataLoading && <Loader className="inline w-3 h-3 animate-spin ml-1" />}
                    </label>
                    <ComboboxMulti
                        id="tradersFilter"
                        dwimOnChange
                        virtualizeList
                        disabled={hasError}
                        options={traderOptions}
                        value={traderIds}
                        onValueChange={(val) => setTraderIds(val)}
                        placeholder={isDataLoading ? "Loading traders..." : hasError ? "Error loading traders" : "Select Trader"}
                        className="w-full"
                        popoverClassName='min-w-[280px] sm:min-w-[320px] md:min-w-[380px] lg:min-w-[420px]'
                        showSelectAll={false}
                    />
                </div>
            )}
            <CustomersFilters
                isDataLoading={isDataLoading}
                hasError={hasError}
                customerOptions={customerOptions}
                customerIds={customerIds}
                setCustomerIds={setCustomerIds}
            />
            {!['activities', 'unknown'].includes(overviewTab) && (
                <div className="relative">
                    <label htmlFor="cropYearFilter" className="block text-sm font-medium text-gray-700">Crop Year</label>
                    <CropYearSelect
                        value={selectedCropYear || ""}
                        onChange={(val) => setSelectedCropYear(val || generateCurrentDefaultCommodityYear())}
                    />
                </div>
            )}
        </>
    );
}

export function BasicFilters({ filtersCollapsed, setFiltersCollapsed }: { filtersCollapsed: boolean; setFiltersCollapsed: any }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedCropYear, setSelectedCropYear] = useQueryParam("crop_year", generateCurrentDefaultCommodityYear());
    const [selectedCommodityDisplayName, setSelectedCommodityDisplayName] = useQueryParam("commodity", "");
    const [customerIds, setCustomerIds] = useQueryParamArray("customer_ids");
    const [overviewTab, _setOverviewTab] = useQueryParam("overview_tab", 'view');
    const [traderIds, setTraderIds] = useQueryParamArray("trader_ids");
    const [detailView, _detailView] = useQueryParam("detail_view", "");
    const [selectedUnknownCard, _setSelectedCard] = useQueryParam("unknown_card", "");

    const [commodityDisplayName, _selectedCommodityDisplayName] = useQueryParam("commodity", "");

    const tradersResult = useListTraders();
    const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });

    const tradersList: Trader[] = tradersResult?.data?.data || [];
    const customersList: { id: string; long_name: string; hasRiskProfile: boolean }[] = (customersResult?.data?.data || []);

    const isUnknownCardView = !!selectedUnknownCard;
    const isCustomerView = customerIds?.length === 1 && !detailView && !selectedCommodityDisplayName && !isUnknownCardView;
    const isCustomerCommodityView = customerIds?.length === 1 && !!selectedCommodityDisplayName && !detailView && !isUnknownCardView;
    const isCustomerCommodityDrilldownView = customerIds?.length === 1 && !!selectedCommodityDisplayName && !!detailView && !isUnknownCardView;

    const { traderOptions } = useMemo(() => {
        return {
            traderOptions: tradersList?.map(trader => ({ value: trader.id, label: trader.name })).sort((a, b) => a.label.localeCompare(b.label)) || [],
        }
    }, [tradersList]);

    const { customerOptions, DisplayCommodityOptions } = useMemo(() => {
        return {
            customerOptions: customersList?.map(customer => ({ value: customer.id, label: customer.long_name })).sort((a, b) => a.label.localeCompare(b.label)) || [],
            DisplayCommodityOptions: [
                { value: "Corn", label: "Corn" },
                { value: "Soybeans", label: "Soybeans" },
                { value: "Wheat", label: "Wheat" },
                { value: "Canola", label: "Canola" },
            ],
        }
    }, [customersList]);

    const isDataLoading = customersResult?.isLoading || tradersResult?.isLoading;
    const hasError = !isDataLoading && (!!customersResult?.error || !!tradersResult?.error);

    return (
        <div className={cn("border rounded-lg p-4 lg:p-2 flex flex-row md:items-center", filtersCollapsed ? "flex flex-row" : "")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-row justify-start items-start gap-2 cursor-pointer">
            <>
                {
                    (isCustomerView) && (
                        <div className="relative">
                            <label htmlFor="backToCustomerCommodityView" className="invisible block text-sm font-medium text-gray-700">Back</label>
                            <Button id="backToCustomerCommodityView" variant="outline" onClick={() => {
                                setSearchParams(prev => {
                                    const newParams = new URLSearchParams(prev);
                                    newParams.delete('customer_ids');
                                    return newParams;
                                });
                            }}>Back</Button>
                        </div>
                    )
                }
                {
                    (isCustomerCommodityDrilldownView) && (
                        <div className="relative">
                            <label htmlFor="backToCustomerCommodityView" className="invisible block text-sm font-medium text-gray-700">Back</label>
                            <Button id="backToCustomerCommodityView" variant="outline" onClick={() => {
                                setSearchParams(prev => {
                                    const newParams = new URLSearchParams(prev);
                                    newParams.delete('detail_view');
                                    return newParams;
                                });
                            }}>Back</Button>
                        </div>
                    )
                }
                {
                    (isCustomerCommodityView) && (
                        <div className="relative">
                            <label htmlFor="backToCustomerCrops" className="invisible block text-sm font-medium text-gray-700">Back</label>
                            <Button id="backToCustomerCrops" variant="outline" onClick={() => {
                                setSearchParams(prev => {
                                    const newParams = new URLSearchParams(prev);
                                    newParams.delete('commodity');
                                    return newParams;
                                });
                            }}>Back</Button>
                        </div>
                    )
                }
                {
                    (isUnknownCardView) && (
                        <div className="relative">
                            <label htmlFor="unknownCardView" className="invisible block text-sm font-medium text-gray-700">Back</label>
                            <Button id="unknownCardView" variant="outline" onClick={() => {
                                setSearchParams(prev => {
                                    const newParams = new URLSearchParams(prev);
                                    newParams.delete('unknown_card');
                                    return newParams;
                                });
                            }}>Back</Button>
                        </div>
                    )
                }
            </>
                {/* <div className="relative">
                    <label htmlFor="clearTradersFilter" className="invisible block text-sm font-medium text-gray-700">Clear Filters</label>
                    <Button id="clearTradersFilter" variant="outline" onClick={() => {
                        setSearchParams(prev => {
                            const newParams = new URLSearchParams(prev);
                            newParams.delete('trader_ids');
                            newParams.delete('customer_ids');
                            newParams.delete('commodity');
                            return newParams;
                        });
                    }}>Clear Filters</Button>
                </div> */}

                <ImportantFilters
                    overviewTab={overviewTab}
                    isDataLoading={isDataLoading}
                    hasError={hasError}
                    traderOptions={traderOptions}
                    customerOptions={customerOptions}
                    traderIds={traderIds}
                    customerIds={customerIds}
                    selectedCropYear={selectedCropYear}
                    setTraderIds={setTraderIds}
                    setCustomerIds={setCustomerIds}
                    setSelectedCropYear={setSelectedCropYear}
                />

                {/* Add Create Crop Profile button when a single customer is selected */}
                <div className="relative">
                    <label className="invisible block text-sm font-medium text-gray-700">Crop Profile</label>
                    <CreateCropProfileDialog
                        useSmallTriggerVariant
                        customerId={customerIds[0]}
                        customerOptions={customerOptions}
                        initialData={selectedCommodityDisplayName ? ({ commodity_name: selectedCommodityDisplayName as any }) : ({})}
                        onSuccess={() => {}}
                    />
                </div>
            </div>
        </div>
    );
}
